import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { approvalFilter } from '@/recoil/atom';
import { useGetFilterRecommend } from '@/hooks/apiHooks';
import { ApprovalFilterType, PeopleFilterName, SearchData } from '@/types/api';
import { EventPageAreaName } from '@/constant/gtm';
import SearchApproval from '@/components/Approval/RightContainer/SearchApproval';
import Favorites from '@/components/Approval/RightContainer/Favorites';
import FilterChipPopover from '@/components/Approval/RightContainer/FilterBar/FilterChipPopover';
import KeywordChipPopover from '@/components/Approval/RightContainer/FilterBar/KeywordChipPopover';
import MoreFiltersPopover from '@/components/Approval/RightContainer/FilterBar/MoreFiltersPopover';
import { getChipSummary } from '@/components/Approval/RightContainer/FilterBar/chipSummary';
import {
  ActionRow,
  ClearAllButton,
  FilterRow,
} from '@/components/Approval/RightContainer/FilterBar/styles';
import {
  ChipSlot,
  useChipMotion,
} from '@/components/Approval/RightContainer/FilterBar/chipMotion';
import CategoryFilterPanel from '@/components/Approval/LeftContainer/CategoryFilter/Panel';
import PeopleFilterPanel from '@/components/Approval/LeftContainer/PeopleFilter/Panel';
import StatusFilterPanel, {
  statusList,
} from '@/components/Approval/LeftContainer/StatusFilter/Panel';
import PeriodFilterPanel from '@/components/Approval/LeftContainer/PeriodFilter/Panel';

type Props = {
  isTablet: boolean;
  onSubmit: ({ keyword, searchFields }: SearchData) => void;
};

/** '필터 더보기'로 켜고 끄는 부가 결재자 필터 (기본 칩 4종 외) */
type OptionalFilter = {
  key: PeopleFilterName;
  label: string;
  recommendKey:
    | 'authorPassports'
    | 'currentApproverPassports'
    | 'finalApproverPassports'
    | 'coApproverPassports';
  allFilterName?: 'requireAllCoApprovers';
};

type KeywordField = 'title' | 'content';
type KeywordFilterKey = 'titleKeyword' | 'contentKeyword';

/**
 * '필터 더보기'로 켜는 키워드 검색 필터 (제목/내용).
 * 제목·내용은 각각 독립 검색어(filters.titleKeyword / filters.contentKeyword)를 가지며,
 * BE titleKeyword/contentKeyword 파라미터로 각각 전달되어 AND 조합된다.
 */
const KEYWORD_FILTERS: {
  key: KeywordField;
  label: string;
  filterKey: KeywordFilterKey;
}[] = [
  { key: 'title', label: '제목', filterKey: 'titleKeyword' },
  { key: 'content', label: '내용', filterKey: 'contentKeyword' },
];

const OPTIONAL_FILTERS: OptionalFilter[] = [
  {
    key: 'authorPassportIds',
    label: '작성자',
    recommendKey: 'authorPassports',
  },
  {
    key: 'currentApproverPassportIds',
    label: '현재 결재자',
    recommendKey: 'currentApproverPassports',
  },
  {
    key: 'finalApproverPassportIds',
    label: '최종 결재자',
    recommendKey: 'finalApproverPassports',
  },
  {
    key: 'coApproverPassportIds',
    label: '협조 결재자',
    recommendKey: 'coApproverPassports',
    allFilterName: 'requireAllCoApprovers',
  },
];

/**
 * 본문 상단 필터바 (브레드크럼 아래 / "총 N건" 위).
 * 1행: 검색 입력 + 양식/결재자/상태/등록기간 칩 + 필터 더보기
 * 2행: 전체해제 + 내 필터로 저장
 * 데이터 배선은 기존 그대로(전역 approvalFilter atom) 재사용하고 배치만 재구성한다.
 */
const FilterBar = ({ isTablet, onSubmit }: Props) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useAtom(approvalFilter);
  const { data: recommend } = useGetFilterRecommend();
  const { reset } = useFormContext<SearchData>();
  const pageArea = EventPageAreaName.WORK_APPROVAL;
  const { slotMotion } = useChipMotion();

  // '필터 더보기'에서 값 없이 추가한 부가 필터 key (값이 있으면 자동으로 칩 노출)
  const [addedKeys, setAddedKeys] = useState<PeopleFilterName[]>([]);
  const isOptionalActive = (f: OptionalFilter) =>
    addedKeys.includes(f.key) || filters[f.key].length > 0;
  const activeOptionals = OPTIONAL_FILTERS.filter(isOptionalActive);
  const activeOptionalKeys = activeOptionals.map((f) => f.key);

  const toggleOptional = (key: PeopleFilterName) => {
    const f = OPTIONAL_FILTERS.find((o) => o.key === key);
    if (!f) return;
    if (isOptionalActive(f)) {
      setAddedKeys((keys) => keys.filter((k) => k !== key));
      setFilters({
        ...filters,
        [f.key]: [],
        ...(f.allFilterName ? { [f.allFilterName]: false } : {}),
      });
    } else {
      setAddedKeys((keys) => [...keys, key]);
    }
  };

  // 제목/내용 키워드 검색 필터 — 각각 독립 검색어(filters.titleKeyword / contentKeyword)를
  // 가지며 BE titleKeyword/contentKeyword 파라미터로 각각 전달되어 AND 조합된다.
  // addedKeywords 는 '값 없이 더보기로 켜둔' 칩(빈 팝오버 상태)을 추적한다.
  const [addedKeywords, setAddedKeywords] = useState<KeywordField[]>([]);

  const filterKeyOf = (key: KeywordField): KeywordFilterKey =>
    key === 'title' ? 'titleKeyword' : 'contentKeyword';

  const isKeywordActive = (key: KeywordField) =>
    addedKeywords.includes(key) || !!filters[filterKeyOf(key)];

  /** 칩 팝오버 검색 → 해당 필드 독립 검색어만 갱신 (조회는 atom 구독으로 자동 갱신) */
  const searchKeyword = (key: KeywordField, keyword: string) => {
    setFilters({ ...filters, [filterKeyOf(key)]: keyword.trim() });
  };

  /** 제목/내용 칩 토글 (더보기 체크 / 칩 X 공통) — 끄면 해당 검색어도 비운다 */
  const toggleKeyword = (key: KeywordField) => {
    if (isKeywordActive(key)) {
      setAddedKeywords((keys) => keys.filter((k) => k !== key));
      setFilters({ ...filters, [filterKeyOf(key)]: '' });
    } else {
      setAddedKeywords((keys) => [...keys, key]);
    }
  };

  // '필터 더보기' 목록 = 결재자류 + 키워드(제목/내용)
  const moreOptions = [
    ...OPTIONAL_FILTERS.map((f) => ({ key: f.key as string, label: f.label })),
    ...KEYWORD_FILTERS.map((f) => ({ key: f.key as string, label: f.label })),
  ];
  const activeKeywordKeys = KEYWORD_FILTERS.filter((f) =>
    isKeywordActive(f.key),
  ).map((f) => f.key);
  const moreActiveKeys: string[] = [
    ...activeOptionalKeys,
    ...activeKeywordKeys,
  ];
  // 더보기에서 갓 꺼낸 칩을 자동으로 열기 위한 신호
  const [autoOpenKey, setAutoOpenKey] = useState<string | null>(null);
  const onMoreToggle = (key: string) => {
    const willAdd = !moreActiveKeys.includes(key);
    if (KEYWORD_FILTERS.some((f) => f.key === key)) {
      toggleKeyword(key as KeywordField);
    } else {
      toggleOptional(key as PeopleFilterName);
    }
    if (willAdd) setAutoOpenKey(key);
  };

  // 통합/제목/내용 검색어는 각각 1개 필터로 카운트
  const keywordCount =
    (filters.keyword.length > 0 ? 1 : 0) +
    (filters.titleKeyword ? 1 : 0) +
    (filters.contentKeyword ? 1 : 0);

  const totalSelectedFilter =
    filters.types.length +
    filters.authorPassportIds.length +
    filters.approverPassportIds.length +
    filters.currentApproverPassportIds.length +
    filters.coApproverPassportIds.length +
    filters.statuses.length +
    filters.endAuthoredAt.length +
    (filters.finalApproverPassportIds?.length ?? 0) +
    keywordCount;
  const haveFilters = totalSelectedFilter > 0;
  const applyFavoriteFilters =
    filters.id === ApprovalFilterType['전체 문서'] && haveFilters;

  const onClearAll = () => {
    setFilters({
      ...filters,
      types: [],
      authorPassportIds: [],
      approverPassportIds: [],
      currentApproverPassportIds: [],
      coApproverPassportIds: [],
      statuses: [],
      endAuthoredAt: [],
      finalApproverPassportIds: [],
      keyword: '',
      searchFields: 'subject,content',
      titleKeyword: '',
      contentKeyword: '',
    });
    setAddedKeys([]);
    setAddedKeywords([]);
    reset();
  };

  return (
    <div style={{ paddingTop: '16px' }}>
      <FilterRow>
        <div style={{ flexShrink: 0, width: 'auto' }}>
          <SearchApproval isTablet={isTablet} onSubmit={onSubmit} />
        </div>
        <FilterChipPopover
          label={t('양식')}
          summary={getChipSummary(filters.types, t)}
          popoverWidth={360}
        >
          <CategoryFilterPanel pageArea={pageArea} title={'양식'} />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('결재자')}
          summary={getChipSummary(filters.approverPassportIds, t)}
          popoverWidth={360}
        >
          <PeopleFilterPanel
            pageArea={pageArea}
            title={'결재자'}
            filterName={'approverPassportIds'}
            recommend={recommend?.approverPassports}
            allFilterName={'requireAllApprovers'}
          />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('상태')}
          summary={getChipSummary(
            // 프리셋은 이미 번역된 이름을 저장하므로 값으로 원문 이름을 다시 찾음
            filters.statuses.map(
              (s) => statusList.find((item) => item.value === s.value) ?? s,
            ),
            t,
            false,
            'status',
          )}
          popoverWidth={280}
        >
          <StatusFilterPanel
            pageArea={pageArea}
            title={'상태'}
            filterName={'statuses'}
          />
        </FilterChipPopover>
        <FilterChipPopover
          label={t('등록기간')}
          summary={getChipSummary(filters.endAuthoredAt, t, true)}
          popoverWidth={420}
          onClear={() => setFilters({ ...filters, endAuthoredAt: [] })}
        >
          <PeriodFilterPanel
            pageArea={pageArea}
            title={'등록기간'}
            filterName={'endAuthoredAt'}
          />
        </FilterChipPopover>
        {/* 필터 더보기로 켜고 끄는 칩 — 갑자기 나타나고 사라지지 않게 폭·투명도를 전환한다 */}
        <AnimatePresence initial={false}>
          {activeOptionals.map((f) => (
            <ChipSlot key={f.key} {...slotMotion}>
              <FilterChipPopover
                label={t(f.label)}
                summary={getChipSummary(filters[f.key], t)}
                popoverWidth={360}
                autoOpen={autoOpenKey === f.key}
                onAutoOpened={() => setAutoOpenKey(null)}
              >
                <PeopleFilterPanel
                  pageArea={pageArea}
                  title={f.label}
                  filterName={f.key}
                  recommend={recommend?.[f.recommendKey]}
                  allFilterName={f.allFilterName}
                />
              </FilterChipPopover>
            </ChipSlot>
          ))}
          {KEYWORD_FILTERS.filter((f) => isKeywordActive(f.key)).map((f) => (
            <ChipSlot key={f.key} {...slotMotion}>
              <KeywordChipPopover
                label={t(f.label)}
                value={filters[f.filterKey] ?? ''}
                onSearch={(keyword) => searchKeyword(f.key, keyword)}
                onClear={() => toggleKeyword(f.key)}
                autoOpen={autoOpenKey === f.key}
                onAutoOpened={() => setAutoOpenKey(null)}
              />
            </ChipSlot>
          ))}
        </AnimatePresence>
        <MoreFiltersPopover
          options={moreOptions}
          activeKeys={moreActiveKeys}
          onToggle={onMoreToggle}
        />
      </FilterRow>

      {(haveFilters || applyFavoriteFilters) && (
        <ActionRow>
          {haveFilters && (
            <ClearAllButton type={'button'} onClick={onClearAll}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10C4 8.11516 4.86911 6.4333 6.22844 5.33333M4 4.99984H6.66667V7.66651"
                  stroke="#0066CC"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('전체해제')}
            </ClearAllButton>
          )}
          {applyFavoriteFilters && <Favorites />}
        </ActionRow>
      )}
    </div>
  );
};

export default FilterBar;
